import { ContentSource, CreatureProfile, ItemMetadata, ItemReference, RuntimeCatalog } from "./schema"

/** Object keys are canonicalized; arrays retain authoring order. */
export const canonicalJson = (value: unknown): string => {
    const normalize = (v: any): any => {
        if (Array.isArray(v)) return v.map(normalize)
        if (v && typeof v === "object") return Object.keys(v).sort().reduce((out, key) => {
            if (v[key] !== undefined) out[key] = normalize(v[key])
            return out
        }, {} as Record<string, unknown>)
        return v
    }
    return JSON.stringify(normalize(value), null, 2) + "\n"
}

/** Resolve template chains without import-time calls into item.ts or models. */
export const resolveProfiles = <T extends { template: string }>(profiles: Record<string, T>, bases: readonly string[]): Record<string, T> => {
    const result: Record<string, T> = Object.create(null)
    const visiting = new Set<string>()
    const visit = (id: string): T => {
        if (result[id]) return result[id]
        if (visiting.has(id)) throw new Error(`Template cycle at ${id}`)
        const profile = profiles[id]
        if (!profile) throw new Error(`Unknown template ${id}`)
        visiting.add(id)
        const inherited = profiles[profile.template] ? visit(profile.template) : undefined
        if (!inherited && !bases.includes(profile.template)) throw new Error(`Unknown template ${profile.template}`)
        result[id] = { ...inherited, ...profile, template: inherited?.template || profile.template }
        visiting.delete(id)
        return result[id]
    }
    Object.keys(profiles).forEach(visit)
    return result
}

export const resolveCatalog = (source: ContentSource, revision: string): RuntimeCatalog => {
    const spriteColors: RuntimeCatalog["spriteColors"] = Object.create(null)
    Object.entries(source.registry).forEach(([slot, components]) => {
        spriteColors[slot] = Object.fromEntries(Object.entries(components).map(([id, entry]) => [id, [...(entry.colors || [])]]))
    })
    const items: RuntimeCatalog["items"] = {
        all: source.items.map(item => item.id), classes: source.classes.map(c => c.id), classToTypes: {}, subClasses: {},
        metadata: {}, resolvedMetadata: {}, sprites: {}, weight: {}, protection: {}, characteristics: {},
        classCharacteristics: {}, trade: {}, recipes: [],
    }
    source.classes.forEach(c => {
        const members = source.items.filter(i => i.class === c.id).map(i => i.id)
        const ordered = (source.presentation.classToTypes[c.id] || []).filter(id => members.includes(id))
        items.classToTypes[c.id] = [...ordered, ...members.filter(id => !ordered.includes(id))]
        items.weight[c.id] = {}; items.protection[c.id] = {}
        if (c.characteristics) items.classCharacteristics[c.id] = c.characteristics
    })
    for (const subclass of new Set([...Object.keys(source.presentation.subClasses), ...source.items.map(i => i.subClass)])) {
        const members = source.items.filter(i => i.subClass === subclass).map(i => i.id)
        const ordered = (source.presentation.subClasses[subclass] || []).filter(id => items.all.includes(id))
        items.subClasses[subclass] = [...ordered, ...members.filter(id => !ordered.includes(id))]
    }
    const slots: Record<string, string> = { weapon: "weapon", pants: "pants", shirt: "shirt", armor: "armor", shoes: "shoes", shield: "shield", cape: "cape", bauldron: "bauldron" }
    source.items.forEach(item => {
        if (item.metadata) items.metadata[item.id] = item.metadata
        if (item.sprite) items.sprites[item.id] = item.sprite
        if (item.weight != null) items.weight[item.class][item.id] = item.weight
        if (item.protection != null) items.protection[item.class][item.id] = item.protection
        if (item.characteristics) items.characteristics[item.id] = item.characteristics
        if (item.trade) items.trade[item.id] = item.trade
        const colors = slots[item.class] ? spriteColors[slots[item.class]]?.[item.id] || [] : ["na"]
        if (item.metadata || colors.length) items.resolvedMetadata[item.id] = item.metadata || colors.map(color => ({ type: item.id, class: item.class, color, ...(item.sprite ? { spriteId: item.sprite } : {}) }))
    })
    const resolveReference = (ref: ItemReference): ItemMetadata => {
        const first = items.resolvedMetadata[ref.item]?.[0]
        if (!first) throw new Error(`Item ${ref.item} has no metadata`)
        // Legacy recipes override the first colour, even when it is not an offered colour.
        return { ...first, ...(ref.color !== undefined ? { color: ref.color } : {}) }
    }
    items.recipes = source.recipes.map(r => ({ itemTypeMeta: resolveReference(r.output), quantity: r.quantity,
        ingredients: r.ingredients.map(i => ({ itemTypeMeta: resolveReference(i.reference), quantity: i.quantity })) }))
    const tiles: RuntimeCatalog["tiles"] = {}
    Object.entries(source.tiles.definitions).forEach(([id, tile]) => {
        tiles[id] = { ...tile, onDestructionCreateItems: tile.onDestructionCreateItems.map(drop => ({ ...drop, itemType: resolveReference(drop.itemType) })) }
    })
    const weaponProfiles = Object.fromEntries(Object.entries(source.sprites.weapons || {}).map(([id, template]) => [id, { template }]))
    const drops = (tables: ContentSource["profiles"]["drops"]) => Object.fromEntries(Object.entries(tables).map(([id, entries]) => [id, entries.map(({ reference, metadata, ...drop }) => ({ ...drop, itemType: reference ? resolveReference(reference) : metadata }))]))
    return { schemaVersion: 1, revision, source, items, spriteColors, tiles, assets: {}, mapTemplates: {}, mapTilesets: {}, npcDrops: drops(source.profiles.drops), itemDrops: drops(source.profiles.itemDrops),
        creatures: resolveProfiles<CreatureProfile>(source.sprites.creatures, source.sprites.registration.RaceTypes),
        weapons: Object.fromEntries(Object.entries(resolveProfiles(weaponProfiles, source.sprites.registration.WeaponTypes)).map(([id, p]) => [id, p.template])),
    }
}
